import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { BaseActionSheet } from './base-action-sheet';
import { Button } from './button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { RadioButtonGroup } from './radio-button-group';

interface RadioButtonActionSheetOptions<T> {
  label: string;
  value: T;
}

interface Props<T> {
  initialSelectedValue: T;
  onApplyChanges: (value: T) => void;
  onClose: () => void;
  options: RadioButtonActionSheetOptions<T>[];
  title: string;
}

export const SingleOptionActionSheet = <T extends string = string>({
  initialSelectedValue,
  onApplyChanges,
  onClose,
  options,
  title,
}: Props<T>) => {
  const [selectedValue, setSelectedValue] = useState<T>(initialSelectedValue);
  const [styles] = useStyles();

  return (
    <BaseActionSheet isVisible onClose={onClose}>
      <RadioButtonGroup<T>
        baseStyle={styles.radioGroup}
        borderBetweenOptions
        onChange={(value) => setSelectedValue(value)}
        options={options.map((opt) => ({
          label: opt.label,
          value: opt.value,
        }))}
        radioButtonOuterStyle={styles.radioButtonOuter}
        rowStyle={styles.optionRow}
        selectedValue={selectedValue}
        textStyle={styles.optionText}
        title={title}
        titleStyle={styles.title}
      />
      <Button
        onPress={() => {
          onApplyChanges(selectedValue);
          onClose();
        }}
        trackingData={{
          name: 'single_option_action_sheet.apply_filters',
        }}
      >
        {i18n.t('apply')}
      </Button>
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  optionRow: {
    marginVertical: 0,
    paddingVertical: 16,
  },
  optionText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.primary,
    fontSize: 16,
  },
  radioButtonOuter: {
    borderColor: theme.colors.inactiveText,
    borderWidth: 2,
  },
  radioGroup: {
    borderTopWidth: 0,
    paddingTop: 0,
  },
  title: {
    ...FONTS.primarySemiBold,
    color: theme.colors.primary,
    fontSize: 25,
    lineHeight: 30,
    textTransform: 'capitalize',
  },
}));
